import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import type { IconType } from '@icons-pack/react-simple-icons';

import { labelToColor } from '../helpers/avatarColor';

interface EntityCardGridProps {
  items: string[];
  getHref: (item: string) => string;
  searchPlaceholder: string;
  emptyMessage: string;
  itemNounPlural: string;
  /** Optional per-item logo (e.g. brand logos); items without one fall back to a letter avatar. */
  getIcon?: (item: string) => IconType | undefined;
}

interface LetterGroup {
  letter: string;
  items: string[];
}

/**
 * Groups items by first letter/digit (case-insensitive). Some source lists sort case-sensitively
 * (e.g. "Izetta" before "i3", "i4"...), so same-letter items aren't always adjacent - group by
 * letter across the whole list rather than assuming runs, to avoid splitting a letter into two
 * sections (and duplicate React keys).
 */
function groupByFirstChar(items: string[]): LetterGroup[] {
  const order: string[] = [];
  const byLetter = new Map<string, string[]>();
  for (const item of items) {
    const letter = item.charAt(0).toUpperCase();
    let bucket = byLetter.get(letter);
    if (!bucket) {
      bucket = [];
      byLetter.set(letter, bucket);
      order.push(letter);
    }
    bucket.push(item);
  }
  return order.map((letter) => ({ letter, items: byLetter.get(letter)! }));
}

function anchorId(itemNounPlural: string, letter: string): string {
  return `${itemNounPlural}-group-${letter}`;
}

function EntityCard({
  item,
  getHref,
  getIcon,
}: {
  item: string;
  getHref: (item: string) => string;
  getIcon?: (item: string) => IconType | undefined;
}) {
  const Icon = getIcon?.(item);
  return (
    <Card sx={{ transition: 'box-shadow 0.15s, transform 0.15s', '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' } }}>
      <CardActionArea component={Link} to={getHref(item)} sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        {Icon ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={22} color="default" title="" aria-hidden="true" />
          </Box>
        ) : (
          <Avatar sx={{ bgcolor: labelToColor(item), fontWeight: 700 }}>{item.charAt(0).toUpperCase()}</Avatar>
        )}
        <Typography variant="subtitle2" fontWeight={600} textAlign="center">
          {item}
        </Typography>
      </CardActionArea>
    </Card>
  );
}

const CARD_GRID_SX = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2 } as const;

export function EntityCardGrid({ items, getHref, searchPlaceholder, emptyMessage, itemNounPlural, getIcon }: EntityCardGridProps) {
  const [query, setQuery] = useState('');
  const isFiltering = query.trim().length > 0;

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return normalized ? items.filter((item) => item.toLowerCase().includes(normalized)) : items;
  }, [items, query]);

  // Grouping/jump-nav only makes sense over the full, unfiltered list - a filtered result set is
  // already short enough to scan directly.
  const groups = useMemo(() => (isFiltering ? [] : groupByFirstChar(filtered)), [filtered, isFiltering]);

  const scrollToGroup = (letter: string) => {
    document.getElementById(anchorId(itemNounPlural, letter))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box>
      <TextField
        fullWidth
        placeholder={searchPlaceholder}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 1, maxWidth: 420 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filtered.length} of {items.length} {itemNounPlural}
      </Typography>

      {filtered.length === 0 ? (
        <Typography color="text.secondary">{emptyMessage}</Typography>
      ) : groups.length > 1 ? (
        <>
          <Box
            sx={{
              position: 'sticky',
              top: 64,
              zIndex: 1,
              display: 'flex',
              overflowX: 'auto',
              gap: 0.5,
              py: 1,
              mb: 3,
              backgroundColor: 'background.default',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {groups.map(({ letter }) => (
              <Box
                key={letter}
                component="button"
                type="button"
                onClick={() => scrollToGroup(letter)}
                sx={{
                  flexShrink: 0,
                  minWidth: 28,
                  height: 28,
                  border: 'none',
                  borderRadius: 1,
                  backgroundColor: 'transparent',
                  color: 'text.secondary',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
                }}
              >
                {letter}
              </Box>
            ))}
          </Box>

          <Stack sx={{ gap: 4 }}>
            {groups.map(({ letter, items: groupItems }) => (
              <Box key={letter} id={anchorId(itemNounPlural, letter)} sx={{ scrollMarginTop: '116px' }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1.5 }}>
                  {letter}
                </Typography>
                <Box sx={CARD_GRID_SX}>
                  {groupItems.map((item) => (
                    <EntityCard key={item} item={item} getHref={getHref} getIcon={getIcon} />
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
        </>
      ) : (
        <Box sx={CARD_GRID_SX}>
          {filtered.map((item) => (
            <EntityCard key={item} item={item} getHref={getHref} />
          ))}
        </Box>
      )}
    </Box>
  );
}
